import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, formatDate } from '@halo/web-lib';
import { useIngestionSSE } from '../../hooks/useIngestionSSE';
import {
  Loader2,
  Globe,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Clock,
  ToggleLeft,
  ToggleRight,
  User,
  Rows3,
  Plus,
  X,
  Search,
  Eye,
  FileText,
} from 'lucide-react';
import type {
  ConfigDetail,
  AmazonConfigData,
  AmazonReportsResponse,
  AmazonReportRow,
  ReportToggleDef,
} from './types';
import {
  AMAZON_MARKETPLACES,
  SELLER_REPORT_TOGGLES,
  ADS_REPORT_TOGGLES,
  REPORT_TYPE_LABELS,
  formatDuration,
} from './types';

interface Props {
  detail: ConfigDetail;
  configId: string;
}

export function AmazonTab({ detail, configId }: Props) {
  const [data, setData] = useState<AmazonConfigData | null>(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState<string | null>(null);

  const isAds = detail.platform === 'amazon_ads';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<AmazonConfigData>(`/api/v1/ingestion/admin/configs/${configId}/amazon`);
      if (res.success && res.data) setData(res.data);
    } catch { /* network error */ }
    setLoading(false);
  }, [configId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400 dark:text-gray-500" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-5">
      <div className="max-w-2xl space-y-5">
        <MarketplaceSection
          configId={configId}
          marketplace={data.marketplace}
          acting={acting}
          setActing={setActing}
          onUpdate={(m) => setData({ ...data, marketplace: m })}
        />

        {isAds && (
          <AdsProfileSection
            storeId={detail.storeId}
            profileId={data.profileId ?? null}
            onRefresh={fetchData}
          />
        )}

        <ReportTogglesSection
          configId={configId}
          platform={detail.platform}
          enabledReportTypes={data.enabledReportTypes}
          acting={acting}
          setActing={setActing}
          onUpdate={(toggles) => setData({ ...data, enabledReportTypes: toggles })}
        />
      </div>

      <ReportManagementSection
        storeId={detail.storeId}
        platform={detail.platform}
        configId={configId}
      />
    </div>
  );
}

/* ─── Marketplace Section ──────────────────────── */

function MarketplaceSection({
  configId,
  marketplace,
  acting,
  setActing,
  onUpdate,
}: {
  configId: string;
  marketplace: string;
  acting: string | null;
  setActing: (v: string | null) => void;
  onUpdate: (marketplace: string) => void;
}) {
  async function handleChange(code: string) {
    setActing('marketplace');
    try {
      await api.put(`/api/v1/ingestion/admin/configs/${configId}/amazon/marketplace`, { marketplace: code });
      onUpdate(code);
    } catch { /* network error */ }
    setActing(null);
  }

  const current = AMAZON_MARKETPLACES.find(m => m.code === marketplace);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-950">
        <Globe className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
        <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Marketplace</span>
      </div>
      <div className="p-4 flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{current?.name ?? marketplace}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Region used for API calls and report generation</div>
        </div>
        <select
          value={marketplace}
          onChange={(e) => handleChange(e.target.value)}
          disabled={acting === 'marketplace'}
          className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:opacity-50"
        >
          {AMAZON_MARKETPLACES.map(m => (
            <option key={m.code} value={m.code}>{m.code} - {m.name}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

/* ─── Ads Profile Section ──────────────────────── */

function AdsProfileSection({
  storeId,
  profileId,
  onRefresh,
}: {
  storeId: number;
  profileId: string | null;
  onRefresh: () => void;
}) {
  const [profiles, setProfiles] = useState<Array<{ profileId: number; countryCode: string; currencyCode: string; accountInfo: { name: string; type: string } }> | null>(null);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [switching, setSwitching] = useState(false);

  async function loadProfiles() {
    setLoadingProfiles(true);
    try {
      const res = await api.get<Array<{ profileId: number; countryCode: string; currencyCode: string; accountInfo: { name: string; type: string } }>>(`/api/v1/oauth/amazon_ads/profiles?store_id=${storeId}`);
      if (res.success && res.data) setProfiles(res.data);
    } catch { /* network error */ }
    setLoadingProfiles(false);
  }

  async function switchProfile(newProfileId: number) {
    setSwitching(true);
    try {
      await api.post('/api/v1/oauth/amazon_ads/select-profile', { storeId, profileId: String(newProfileId) });
      onRefresh();
    } catch { /* network error */ }
    setSwitching(false);
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-950">
        <User className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
        <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Ads Profile</span>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {profileId ? `Profile ${profileId}` : 'No profile selected'}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Used as Amazon-Advertising-API-Scope header
            </div>
          </div>
          <button
            onClick={loadProfiles}
            disabled={loadingProfiles}
            className="px-2.5 py-1 text-xs font-medium border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-950 disabled:opacity-50 flex items-center gap-1"
          >
            {loadingProfiles ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {profiles ? 'Refresh' : 'Load Profiles'}
          </button>
        </div>

        {profiles && profiles.length > 0 && (
          <div className="space-y-1.5">
            {profiles.map(p => {
              const isCurrent = String(p.profileId) === profileId;
              return (
                <div
                  key={p.profileId}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
                    isCurrent ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div>
                    <div className="text-xs font-medium text-gray-800 dark:text-gray-100">{p.accountInfo.name}</div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">{p.countryCode} - {p.currencyCode} - {p.accountInfo.type}</div>
                  </div>
                  {isCurrent ? (
                    <span className="text-[10px] font-medium text-green-600 dark:text-green-400 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Active
                    </span>
                  ) : (
                    <button
                      onClick={() => switchProfile(p.profileId)}
                      disabled={switching}
                      className="px-2 py-0.5 text-[10px] font-medium bg-gray-900 dark:bg-gray-950 text-white rounded hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50"
                    >
                      Switch
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Report Toggles Section ───────────────────── */

function ReportTogglesSection({
  configId,
  platform,
  enabledReportTypes,
  acting,
  setActing,
  onUpdate,
}: {
  configId: string;
  platform: string;
  enabledReportTypes: Record<string, boolean>;
  acting: string | null;
  setActing: (v: string | null) => void;
  onUpdate: (toggles: Record<string, boolean>) => void;
}) {
  const toggles: ReportToggleDef[] = platform === 'amazon_seller' ? SELLER_REPORT_TOGGLES : ADS_REPORT_TOGGLES;

  async function handleToggle(key: string, enabled: boolean) {
    const updated = { ...enabledReportTypes, [key]: enabled };
    setActing(`toggle-${key}`);
    try {
      await api.put(`/api/v1/ingestion/admin/configs/${configId}/amazon/report-toggles`, {
        enabledReportTypes: updated,
      });
      onUpdate(updated);
    } catch { /* network error */ }
    setActing(null);
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-950">
        <Rows3 className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
        <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Report Types</span>
      </div>
      <div className="divide-y divide-gray-100">
        {toggles.map(t => {
          const enabled = enabledReportTypes[t.key] ?? t.defaultEnabled;
          const isActing = acting === `toggle-${t.key}`;
          return (
            <div key={t.key} className="flex items-center justify-between px-4 py-3">
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.label}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t.description}</div>
              </div>
              <button
                onClick={() => handleToggle(t.key, !enabled)}
                disabled={isActing}
                className="shrink-0 disabled:opacity-50"
              >
                {isActing ? (
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400 dark:text-gray-500" />
                ) : enabled ? (
                  <ToggleRight className="w-7 h-7 text-green-600 dark:text-green-400" />
                ) : (
                  <ToggleLeft className="w-7 h-7 text-gray-300" />
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Report Management Section ───────────────── */

const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  completed: { bg: 'bg-green-100 dark:bg-green-950/40', text: 'text-green-700 dark:text-green-400' },
  processing: { bg: 'bg-blue-100 dark:bg-blue-950/40', text: 'text-blue-700 dark:text-blue-400' },
  pending: { bg: 'bg-amber-100 dark:bg-amber-950/40', text: 'text-amber-700 dark:text-amber-300' },
  failed: { bg: 'bg-red-100 dark:bg-red-950/40', text: 'text-red-700 dark:text-red-400' },
};

function ReportManagementSection({
  storeId,
  platform,
  configId,
}: {
  storeId: number;
  platform: string;
  configId: string;
}) {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [acting, setActing] = useState<string | null>(null);

  const queryParams = new URLSearchParams({
    storeId: String(storeId),
    platform,
    limit: '50',
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(typeFilter ? { reportType: typeFilter } : {}),
  });

  const queryString = queryParams.toString();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin', 'ingestion', 'amazon-reports', storeId, platform, statusFilter, typeFilter],
    queryFn: async ({ signal }) => {
      const res = await api.get<AmazonReportsResponse>(
        `/api/v1/ingestion/admin/amazon-reports?${queryString}`,
        { signal },
      );
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load reports');
      return res.data;
    },
  });

  // Auto-refresh if any reports are processing (slow fallback — SSE is primary)
  useEffect(() => {
    if (!data?.stats.processing) return;
    const interval = setInterval(() => { refetch(); }, 60_000);
    return () => clearInterval(interval);
  }, [data?.stats.processing, refetch]);

  // SSE real-time updates
  useIngestionSSE({
    storeId,
    platform,
    onEvent: useCallback(() => {
      refetch();
    }, [refetch]),
  });

  const typeOptions = platform === 'amazon_seller'
    ? ['salesAndTraffic', 'orderLevelData']
    : ['sp', 'sb', 'sd', 'dsp'];

  async function handleRetry(report: AmazonReportRow) {
    setActing(`retry-${report.amazonReportId}`);
    try {
      await api.post(`/api/v1/ingestion/admin/configs/${configId}/amazon/retry-report`, {
        runId: report.runId,
        reportType: report.reportType,
      });
      refetch();
    } catch { /* network error */ }
    setActing(null);
  }

  async function handleCheck(report: AmazonReportRow) {
    setActing(`check-${report.amazonReportId}`);
    try {
      await api.post(`/api/v1/ingestion/admin/amazon-reports/${report.amazonReportId}/check`, {
        runId: report.runId,
        platform: report.platform,
        storeId: report.storeId,
      });
      // Poll after 3s to allow the check to complete
      setTimeout(refetch, 3000);
    } catch { /* network error */ }
    setActing(null);
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-950">
        <div className="flex items-center gap-2">
          <FileText className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
          <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Reports</span>
          {data?.stats && (
            <div className="flex items-center gap-1.5 ml-2">
              {data.stats.processing > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 font-medium">
                  {data.stats.processing} processing
                </span>
              )}
              {data.stats.failed > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400 font-medium">
                  {data.stats.failed} failed
                </span>
              )}
              <span className="text-[10px] text-gray-400 dark:text-gray-500">{data.stats.total} total</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { refetch(); }}
            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded"
            title="Refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-gray-900 dark:bg-gray-950 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800"
          >
            <Plus className="w-3 h-3" />
            Create Report
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100 dark:border-gray-800">
        <Search className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="text-xs px-2 py-1 border border-gray-200 dark:border-gray-800 rounded-md bg-white dark:bg-gray-900"
        >
          <option value="">All statuses</option>
          <option value="processing">Processing</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="pending">Pending</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="text-xs px-2 py-1 border border-gray-200 dark:border-gray-800 rounded-md bg-white dark:bg-gray-900"
        >
          <option value="">All types</option>
          {typeOptions.map(t => (
            <option key={t} value={t}>{REPORT_TYPE_LABELS[t] ?? t}</option>
          ))}
        </select>
        {(statusFilter || typeFilter) && (
          <button
            onClick={() => { setStatusFilter(''); setTypeFilter(''); }}
            className="text-[10px] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
          >
            Clear
          </button>
        )}
      </div>

      {/* Table */}
      {isLoading && !data ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400 dark:text-gray-500" />
        </div>
      ) : !data || data.reports.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-400 dark:text-gray-500">
          No reports found
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50/50">
                <th className="text-left px-4 py-2 font-medium text-gray-500 dark:text-gray-400">Status</th>
                <th className="text-left px-4 py-2 font-medium text-gray-500 dark:text-gray-400">Report Type</th>
                <th className="text-left px-4 py-2 font-medium text-gray-500 dark:text-gray-400">Date Range</th>
                <th className="text-left px-4 py-2 font-medium text-gray-500 dark:text-gray-400">Report ID</th>
                <th className="text-left px-4 py-2 font-medium text-gray-500 dark:text-gray-400">Created</th>
                <th className="text-left px-4 py-2 font-medium text-gray-500 dark:text-gray-400">Poll Status</th>
                <th className="text-left px-4 py-2 font-medium text-gray-500 dark:text-gray-400">Duration</th>
                <th className="text-right px-4 py-2 font-medium text-gray-500 dark:text-gray-400">Rows</th>
                <th className="text-left px-4 py-2 font-medium text-gray-500 dark:text-gray-400">Source</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.reports.map((r, i) => {
                const style = STATUS_STYLES[r.status] ?? STATUS_STYLES['pending']!;
                return (
                  <tr key={`${r.amazonReportId ?? r.reportType}-${r.createdAt}-${i}`} className="hover:bg-gray-50/50">
                    <td className="px-4 py-2.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${style.bg} ${style.text}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-gray-100">
                      {REPORT_TYPE_LABELS[r.reportType] ?? r.reportType}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400">
                      {r.windowStart && r.windowEnd
                        ? r.windowStart === r.windowEnd
                          ? r.windowStart
                          : `${r.windowStart} - ${r.windowEnd}`
                        : '-'}
                    </td>
                    <td className="px-4 py-2.5 text-gray-400 dark:text-gray-500 font-mono text-[10px]">
                      {r.amazonReportId ? r.amazonReportId.slice(0, 16) + '...' : '-'}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400">{formatDate(r.createdAt)}</td>
                    <td className="px-4 py-2.5">
                      {r.status === 'processing' ? (
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            <span className="text-[10px] font-medium">{r.amazonStatus ?? 'PROCESSING'}</span>
                          </div>
                          <div className="text-[10px] text-gray-400 dark:text-gray-500">
                            {r.pollAttempts != null ? `Poll #${r.pollAttempts}` : ''}
                            {r.lastPolledAt ? ` · ${timeAgo(r.lastPolledAt)}` : ''}
                          </div>
                        </div>
                      ) : r.status === 'completed' ? (
                        <span className="text-[10px] text-green-600 dark:text-green-400 font-medium">DONE</span>
                      ) : r.status === 'failed' ? (
                        <div className="space-y-0.5">
                          <span className="text-[10px] text-red-600 dark:text-red-400 font-medium">{r.amazonStatus ?? 'FAILED'}</span>
                          {r.pollAttempts != null && (
                            <div className="text-[10px] text-gray-400 dark:text-gray-500">after {r.pollAttempts} polls</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-[10px] text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400">
                      {r.duration != null ? formatDuration(r.duration) : r.status === 'processing' ? (
                        <span className="flex items-center gap-1 text-blue-500">
                          <Loader2 className="w-3 h-3 animate-spin" />
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-200 font-medium">
                      {r.rowCount != null ? r.rowCount.toLocaleString() : '-'}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-[10px] text-gray-400 dark:text-gray-500">{r.triggerSource}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1 justify-end">
                        {r.status === 'processing' && r.amazonReportId && (
                          <button
                            onClick={() => handleCheck(r)}
                            disabled={acting === `check-${r.amazonReportId}`}
                            title="Check status now"
                            className="p-1 text-blue-500 hover:text-blue-700 dark:hover:text-blue-400 disabled:opacity-50 rounded"
                          >
                            {acting === `check-${r.amazonReportId}` ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Eye className="w-3.5 h-3.5" />
                            )}
                          </button>
                        )}
                        {r.status === 'failed' && (
                          <button
                            onClick={() => handleRetry(r)}
                            disabled={acting === `retry-${r.amazonReportId}`}
                            title="Retry report"
                            className="p-1 text-red-500 hover:text-red-700 dark:hover:text-red-400 disabled:opacity-50 rounded"
                          >
                            {acting === `retry-${r.amazonReportId}` ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <RefreshCw className="w-3.5 h-3.5" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Error rows expanded */}
          {data.reports.some(r => r.errorMessage) && (
            <div className="border-t border-gray-100 dark:border-gray-800 px-4 py-2">
              {data.reports.filter(r => r.errorMessage).map((r, i) => (
                <div key={`err-${i}`} className="flex items-start gap-2 py-1">
                  <AlertCircle className="w-3 h-3 text-red-400 mt-0.5 shrink-0" />
                  <div className="text-[10px] text-red-600 dark:text-red-400">
                    <span className="font-medium">{REPORT_TYPE_LABELS[r.reportType] ?? r.reportType}:</span>{' '}
                    {r.errorMessage}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showCreateModal && (
        <CreateReportModal
          storeId={storeId}
          platform={platform}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}

function timeAgo(isoDate: string): string {
  const secs = Math.floor((Date.now() - new Date(isoDate).getTime()) / 1000);
  if (secs < 10) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
}

/* ─── Create Report Modal ─────────────────────── */

function CreateReportModal({
  storeId,
  platform,
  onClose,
  onCreated,
}: {
  storeId: number;
  platform: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const typeOptions = platform === 'amazon_seller'
    ? SELLER_REPORT_TOGGLES
    : ADS_REPORT_TOGGLES;

  // Default: all enabled
  useEffect(() => {
    setSelectedTypes(new Set(typeOptions.filter(t => t.defaultEnabled).map(t => t.key)));
  }, [platform]);

  function toggleType(key: string) {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function handleCreate() {
    if (!startDate || !endDate || selectedTypes.size === 0) {
      setError('Select dates and at least one report type');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const res = await api.post('/api/v1/ingestion/admin/amazon-reports/create', {
        storeId,
        platform,
        startDate,
        endDate,
        reportTypes: [...selectedTypes],
      });
      if (res.success) {
        onCreated();
      } else {
        setError(res.error ?? 'Failed to create reports');
      }
    } catch {
      setError('Network error');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Create Amazon Report</h3>
          <button onClick={onClose} className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Start Date</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={today}
                className="mt-1 block w-full px-2.5 py-1.5 text-sm border border-gray-200 dark:border-gray-800 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">End Date</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                max={today}
                className="mt-1 block w-full px-2.5 py-1.5 text-sm border border-gray-200 dark:border-gray-800 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900"
              />
            </label>
          </div>

          <div>
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Report Types</span>
            <div className="mt-1.5 space-y-1.5">
              {typeOptions.map(t => (
                <label
                  key={t.key}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-950 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedTypes.has(t.key)}
                    onChange={() => toggleType(t.key)}
                    className="rounded text-gray-900 dark:text-gray-100 focus:ring-gray-900"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-100">{t.label}</div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">{t.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="text-xs text-gray-400 dark:text-gray-500">
            Store #{storeId} · {platform}
          </div>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-800">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100">
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={creating || selectedTypes.size === 0}
            className="px-4 py-2 text-sm font-medium bg-gray-900 dark:bg-gray-950 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            {creating ? 'Creating...' : 'Create Reports'}
          </button>
        </div>
      </div>
    </div>
  );
}
